import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import { useTenant } from "../../Contexts/TenantContext";
import { TenantPreference } from "../../Models/TenantPreference";

interface AdminMenuProps {
  variant?: "default" | "alt";
}

const AdminMenu = ({ variant = "default" }: AdminMenuProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { tenantPreference } = useTenant();
  const [showMore, setShowMore] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const isMoreHoveredRef = useRef(false);

  const isActive = (path: string) =>
    path === "/admin"
      ? pathname === "/admin"
      : path === "/admin/dados"
      ? pathname === "/admin/dados"
      : pathname === path || pathname.startsWith(`${path}/`);

  const navItemClass = (path: string) =>
    variant === "alt"
      ? `py-2 sm:py-0 px-3 sm:px-0 hover:text-primary-600 ${isActive(path) ? "text-primary-600" : "text-gray-800"}`
      : `py-2 sm:py-0 px-3 sm:px-0 hover:text-gray-300 lg:hover:text-white lg:underline-offset-4 lg:decoration-2 lg:hover:underline ${isActive(path) ? "text-gray-300 lg:text-white lg:underline" : ""}`;

  const handleNavClick = (path: string, closeMenu?: () => void) => (event: React.MouseEvent) => {
    if (isActive(path)) {
      event.preventDefault();
      navigate(path, { replace: true, state: { refreshKey: Date.now() } });
      closeMenu?.();
      return;
    }
    closeMenu?.();
  };

  type FeaturePreferenceKey =
    | "enableOperatorsFeature"
    | "enableBrandsFeature"
    | "enableCategoriesFeature"
    | "enableModelsFeature";

  const items: Array<{ to: string; label: string; requiresFlag?: boolean; requiresPreference?: FeaturePreferenceKey }> = [
    { to: "/admin", label: "INÍCIO" },
    { to: "/admin/operadores", label: "OPERADORES", requiresPreference: "enableOperatorsFeature" },
    { to: "/admin/clientes", label: "CLIENTES" },
    { to: "/admin/marcas", label: "MARCAS", requiresPreference: "enableBrandsFeature" },
    { to: "/admin/categorias", label: "CATEGORIAS", requiresPreference: "enableCategoriesFeature" },
    { to: "/admin/modelos", label: "MODELOS", requiresPreference: "enableModelsFeature" },
    { to: "/admin/produtos", label: "PRODUTOS" },
    { to: "/admin/pedidos", label: "PEDIDOS" },
    { to: "/admin/cupons", label: "CUPONS" },
    { to: "/admin/whatsapp", label: "WHATSAPP" },
    { to: "/admin/faturamento", label: "FATURAMENTO" },
    { to: "/admin/balcao", label: "BALCÃO" },
  ];

  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== "undefined" ? window.innerWidth : 1920);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!showMore) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!moreMenuRef.current) {
        return;
      }

      if (!moreMenuRef.current.contains(event.target as Node)) {
        setShowMore(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowMore(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showMore]);

  // Measured with the logo beside the menu, leaving the bar's side padding free
  const visibleCompact =
    windowWidth >= 1680 ? 13 :
    windowWidth >= 1580 ? 12 :
    windowWidth >= 1510 ? 11 :
    windowWidth >= 1400 ? 10 :
    windowWidth >= 1270 ? 9 :
    windowWidth >= 1190 ? 8 :
    windowWidth >= 1095 ? 7 : 6;
  const filteredItems = items.filter(item => {
    if (item.requiresFlag && !tenantPreference?.enableTechnicalAssistancePage) {
      return false;
    }

    if (!item.requiresPreference) {
      return true;
    }

    return (tenantPreference?.[item.requiresPreference as keyof TenantPreference] as boolean | undefined) !== false;
  });
  const isMobile = windowWidth < 1024;
  const primaryItems = isMobile ? filteredItems : filteredItems.slice(0, visibleCompact);
  const overflowItems = isMobile ? [] : filteredItems.slice(visibleCompact);
  const useCompactEverywhere = !isMobile && filteredItems.length > visibleCompact;

  return (
    <nav className="w-full">
      {!useCompactEverywhere && (
        <ul className={`hidden lg:flex lg:justify-center font-bold text-sm flex-wrap items-center gap-x-6 gap-y-2 leading-tight px-4`}>
          {filteredItems.map(item => (
            <li key={item.to} className={navItemClass(item.to)}>
              <Link to={item.to} onClick={handleNavClick(item.to)}>{item.label}</Link>
            </li>
          ))}
        </ul>
      )}

      {/* Compact menu with More */}
      <div className={`flex items-center lg:justify-center gap-3 px-3 py-3 lg:py-0 ${useCompactEverywhere ? '' : 'lg:hidden'}`}>
        <ul className="font-bold text-sm grid grid-cols-3 lg:flex lg:flex-wrap lg:items-center gap-y-2 gap-x-2 lg:gap-x-5 leading-tight w-full lg:w-auto">
          {primaryItems.map(item => (
            <li key={item.to} className={navItemClass(item.to)}>
              <Link to={item.to} onClick={handleNavClick(item.to)}>{item.label}</Link>
            </li>
          ))}
        </ul>

        {overflowItems.length > 0 && (
          <div
            className="relative"
            ref={moreMenuRef}
            onPointerEnter={(event) => {
              if (event.pointerType === "mouse") {
                isMoreHoveredRef.current = true;
                setShowMore(true);
              }
            }}
            onPointerLeave={(event) => {
              if (event.pointerType === "mouse") {
                isMoreHoveredRef.current = false;
                setShowMore(false);
              }
            }}
          >
            <button
              className={variant === "alt"
                ? `flex items-center gap-2 font-bold text-sm px-2 py-1 rounded border border-gray-300 text-gray-800 hover:bg-gray-100`
                : `flex items-center gap-2 font-bold text-sm px-2 py-1 rounded border border-white/40 hover:bg-white/10`}
              onClick={() => setShowMore((prev) => isMoreHoveredRef.current || !prev)}
              aria-expanded={showMore}
              aria-haspopup="true"
            >
              {"MAIS"}
              <FaChevronDown className={`text-xs transition-transform ${showMore ? "rotate-180" : ""}`} />
            </button>
            {showMore && (
              // The top padding keeps the pointer inside the menu on its way down from the button
              <div className="absolute right-0 top-full pt-4 z-50">
                <div className="relative bg-white rounded shadow-lg min-w-[160px] text-primary-600">
                  <div className="absolute -top-2 right-4 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-200" />
                  <ul className="relative divide-y divide-gray-200 rounded overflow-hidden">
                    {overflowItems.map(item => (
                      <li
                        key={item.to}
                        className={`font-bold text-sm whitespace-nowrap hover:bg-primary-120`}
                      >
                        <Link
                          to={item.to}
                          onClick={handleNavClick(item.to, () => setShowMore(false))}
                          className="block px-3 py-2"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default AdminMenu;
